import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Player, PlayersServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";

type PreviewDevice = "web" | "mobile";

const MOBILE_PREVIEW_WIDTH = 390;

const PLAYER_ID_PLACEHOLDER = "{{player_id}}";

const usePreviewPlayer = () => {
  const [previewPlayer, setPreviewPlayer] = useState<Player | null>(null);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("web");

  const getPlayers = useCallback(async (search: string | undefined, page: number) => {
    return PlayersServices.getPlayers({
      search,
      page,
      perPage: 25,
      sort: "first_name",
      sortDirection: "asc",
    });
  }, []);

  const players = usePaginatedCombobox(getPlayers, false, previewPlayer);
  useEffect(() => {
    setPreviewPlayer(players.value ?? null);
  }, [players.value]);

  return { players, previewPlayer, previewDevice, setPreviewDevice };
};

type PreviewPlayerState = ReturnType<typeof usePreviewPlayer>;

interface PlayerPreviewQuery {
  playerGuid?: string;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

const usePlayerPreviewLoading = ({ playerGuid, isFetching, isError, error }: PlayerPreviewQuery) => {
  const [loadedForGuid, setLoadedForGuid] = useState<string | null>(null);

  useEffect(() => {
    if (!isError) return;
    console.error(error);
    toast.error("Error loading player preview");
  }, [isError, error]);

  useEffect(() => {
    if (!playerGuid) setLoadedForGuid(null);
    else if (!isFetching) setLoadedForGuid(playerGuid);
  }, [playerGuid, isFetching]);

  return !!playerGuid && playerGuid !== loadedForGuid;
};

export { usePreviewPlayer, usePlayerPreviewLoading, MOBILE_PREVIEW_WIDTH, PLAYER_ID_PLACEHOLDER };
export type { PreviewDevice, PreviewPlayerState };
